import React, { useMemo } from "react"
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
  from,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { GraphQLClientContext } from "./DualGraphQLProvider"

/**
 * Share-scoped Apollo transport for the login-free public report view.
 *
 * Unlike {@link DualGraphQLProvider}'s authenticated client — which reads the
 * better-auth bearer token from localStorage and sets `authorization` +
 * `X-Auth-Type` — this client authenticates with an opaque share token. Its
 * `authLink` injects a single `X-Share-Token: <token>` header and deliberately
 * OMITS `authorization` and `X-Auth-Type`, so the request can only ever resolve
 * through the backend's positive share-token path (never the bearer paths and
 * never the anonymous fall-through).
 *
 * The client is pointed at the primary (experience) endpoint and surfaced as
 * `GraphQLClientContext.primaryClient`, so the shared report hooks
 * (`useReport` / `useEntityDataRange`) that select the `"primary"` client via
 * `useGraphQLClient("primary")` resolve to it unchanged in the public view.
 *
 * Requirements: 8.1 (token-scoped transport), 11.1 (treat the share token as a
 * bearer credential — the token alone grants read-only access).
 */
export interface ShareGraphQLProviderProps {
  /** The primary (experience) GraphQL endpoint — same URI the hub uses. */
  endpoint: string
  /** The opaque share token (nanoid) sent as `X-Share-Token`. */
  token: string
  cacheConfig?: InMemoryCacheConfig
  children: React.ReactNode
}

/**
 * Build an {@link ApolloClient} that authenticates with a share token.
 *
 * Modelled on `DualGraphQLProvider`'s `createApolloClient` — same `httpLink`,
 * `errorLink`, cache `typePolicies`, and `defaultOptions` — the only departure
 * is the auth link, which swaps the bearer `authorization` / `X-Auth-Type`
 * headers for a single `X-Share-Token` header.
 */
export function createShareApolloClient(
  uri: string,
  token: string,
  cacheConfig?: InMemoryCacheConfig,
): ApolloClient<any> {
  const httpLink = new HttpLink({ uri })

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      )
    if (networkError) console.log(`[Network error]: ${networkError}`)
  })

  // Share-token auth link: inject only `X-Share-Token`. We intentionally do NOT
  // set `authorization` or `X-Auth-Type` — a public viewer has no bearer token,
  // and sending neither keeps the request on the backend's share-token path.
  const authLink = setContext(async (_, { headers }) => ({
    headers: {
      ...headers,
      "X-Share-Token": token,
    },
  }))

  return new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache({
      ...cacheConfig,
      typePolicies: {
        Asset: {
          fields: {
            class: {
              merge(existing, incoming) {
                return incoming ?? existing
              },
            },
            kind: {
              merge(existing, incoming) {
                if (!incoming || (typeof incoming === "string" && incoming.toLowerCase() === "other")) {
                  return existing ?? incoming
                }
                return incoming
              },
            },
            category: {
              merge(existing, incoming) {
                return incoming ?? existing
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
        nextFetchPolicy: "cache-first",
        errorPolicy: "all",
        refetchWritePolicy: "overwrite",
      },
      query: {
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      },
      mutate: {
        errorPolicy: "all",
      },
    },
  })
}

/**
 * Mount a share-scoped Apollo client for the public report view.
 *
 * The share client is both the Apollo default client (via `ApolloProvider`)
 * and `GraphQLClientContext.primaryClient`, so any shared component selecting
 * the `"primary"` client resolves to it. There is no legacy client in the
 * public view — the share token only grants the two allow-listed experience
 * queries — so `legacyClient` is set to the same share client.
 */
export function ShareGraphQLProvider({ endpoint, token, cacheConfig, children }: ShareGraphQLProviderProps) {
  const shareClient = useMemo(
    () => createShareApolloClient(endpoint, token, cacheConfig),
    [endpoint, token, cacheConfig]
  )

  const contextValue = useMemo(
    () => ({
      primaryClient: shareClient,
      legacyClient: shareClient,
      currentEndpoint: "primary" as const,
    }),
    [shareClient]
  )

  return (
    <ApolloProvider client={shareClient}>
      <GraphQLClientContext.Provider value={contextValue}>{children}</GraphQLClientContext.Provider>
    </ApolloProvider>
  )
}

export default ShareGraphQLProvider
